import React, {useState} from "react";
import {
  Autocomplete,
  Box,
  Checkbox,
  FormControlLabel,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {HugeiconsIcon} from "@hugeicons/react";
import {Settings01Icon} from "@hugeicons/core-free-icons";
import {menuItemsInfo} from "../editMenuItems";
import {v4 as uuidv4} from "uuid";
import {NodeType} from "../../../../../model/ModelData";
import MultiSelectAutocomplete from "../../../../../components/inputs/multi-select-autocomplete";
import TagManagementModal from "../components/tag-management-modal";
import {UI, rgba} from "../../uiTokens";
import {checkboxSx, controlLabelSx, fieldSx, RouteSection, sectionLabelSx} from "./routeStyles";
import {RouteParamsList} from "./RouteParamsList";

const staticRouteDescription = {
  next: "This route will be next when node finishes",
  no_match: "This route will only be executed if we don't find a any route (if we don't catch any of the intents)",
  fail: "This route is executed if any error occurs",
};

export const StaticRoutesTab = ({node, setNode, nodes, onUpdate, project, flow, selectedRoute, routes = ["next", "no_match", "fail"]}) => {
  const [manageTagsOpen, setManageTagsOpen] = useState(false);

  const createRoute = (route) => {
    return {
      id: uuidv4(),
      target: null,
      name: null,
      type: route,
      backstack: false,
      data: {
        greet: "",
        params: [],
        is_enabled_cbw: false,
        conversation_buffer_window: 0,
        tags: [],
      },
    };
  };

  let items = nodes.filter(
    item => node?.id !== item.id
      && ![NodeType.END_FLOW].includes(item.type),
  );
  if (node?.data?.form_id && node?.type !== NodeType.END_FORM) {
    // items = items.filter(i => i?.data?.form_id === node?.data?.form_id);
  } else {
    items = items?.filter(i => !i?.data?.form_id || i?.type === NodeType.START_FORM);
  }
  items = items?.map(node => ({...node, groupName: `Flow: ${flow?.name}`}));
  if (node?.data?.form_id) {
    const formNode = items?.find(item => item?.data?.form_id === node?.data?.form_id && item?.type === NodeType.START_FORM);
    if (node?.type === NodeType.END_FORM) {
      items = items?.map(item => {
        if (item?.data?.form_id === node?.data?.form_id) {
          return {...item, groupName: `Form: ${formNode?.name || flow?.name}`};
        }
        return {...item};
      });
    } else {
      items = items?.filter(item => item?.data?.form_id === node?.data?.form_id)?.map(item => ({...item, groupName: `Form: ${formNode?.name || flow?.name}`}));
    }
  }
  items = items.sort((a, b) => -b.groupName.localeCompare(a.groupName));

  const {primary, secondary} = menuItemsInfo.static;

  return (
    <>
      <TagManagementModal
        open={manageTagsOpen}
        handleClose={() => setManageTagsOpen(false)}
        tags={project?.settings?.tags || []}
        project={project}
      />

      <Box>
        <Typography sx={{fontWeight: 700, fontSize: 14, color: UI.ink}}>{primary}</Typography>
        <Typography sx={{mt: 0.25, fontSize: 12, color: UI.muted, lineHeight: 1.45}}>{secondary}</Typography>
      </Box>

      {routes?.map(route => {
        const staticRoute = node?.routes?.static?.[route];
        const selectedNode = nodes?.find(n => n?.id === staticRoute?.target);
        const isConnected = !!selectedNode;

        return (
          <RouteSection
            key={route}
            title={route?.replaceAll("_", " ")}
            description={staticRouteDescription[route]}
            connected={isConnected}
            connectedLabel={selectedNode?.name}
            defaultExpanded={staticRoute?.id === selectedRoute}
          >
            <Autocomplete
              id={`${route}-select`}
              size="small"
              options={items}
              groupBy={option => option?.groupName}
              getOptionLabel={option => option?.name || ""}
              isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
              value={selectedNode || null}
              blurOnSelect
              slotProps={{
                paper: {
                  sx: {
                    mt: 0.5,
                    borderRadius: "10px",
                    border: `1px solid ${rgba(UI.accent, 0.16)}`,
                    boxShadow: UI.shadow,
                  },
                },
              }}
              renderInput={(props) => <TextField {...props} size="small" label="Target node" sx={fieldSx}/>}
              renderGroup={params => (
                <li key={params.key}>
                  <Typography
                    sx={{
                      ...sectionLabelSx,
                      px: 1.25,
                      py: 0.75,
                      position: "sticky",
                      top: 0,
                      zIndex: 1,
                      backgroundColor: UI.surface,
                    }}
                  >
                    {params.group}
                  </Typography>
                  <Box sx={{px: 0.5}}>{params.children}</Box>
                </li>
              )}
              onChange={(e, value) => {
                const newNode = {...node};
                const selectedNode = value;
                if (selectedNode) {
                  let next = {...newNode.routes.static[route]};
                  if (!next) {
                    next = createRoute(route);
                  }
                  next.id = next?.id || uuidv4();
                  next.target = selectedNode.id;
                  next.name = selectedNode.name;
                  newNode.routes.static[route] = next;
                } else {
                  let next = {...newNode.routes.static[route]};
                  if (!next) {
                    next = createRoute(route);
                  }
                  next.id = next?.id || uuidv4();
                  next.target = null;
                  next.name = "";
                  newNode.routes.static[route] = next;
                }
                onUpdate(newNode);
              }}
            />

            <TextField
              label="Greet message"
              size="small"
              fullWidth
              sx={fieldSx}
              value={node?.routes?.static[route]?.data?.greet || ""}
              onChange={(e) => {
                setNode(prev => {
                  const newNode = {...prev};
                  let next = {...newNode.routes.static[route]};
                  if (!next) {
                    next = createRoute(route);
                  }
                  const data = {...next.data};
                  data.greet = e.target.value;

                  next.id = next?.id || uuidv4();
                  next.data = data;
                  newNode.routes.static[route] = next;
                  return newNode;
                });
              }}
              onBlur={() => onUpdate(node)}
            />

            <FormControlLabel
              sx={{ml: -0.5, mr: 0}}
              control={
                <Checkbox
                  size="small"
                  sx={checkboxSx}
                  onChange={(e, value) => {
                    setNode(prev => {
                      const newNode = {...prev};
                      let next = {...newNode.routes.static[route]};
                      if (!next) {
                        next = createRoute(route);
                      }
                      const data = {...next.data};
                      data.is_enabled_cbw = value;
                      data.conversation_buffer_window = 0;

                      next.id = next?.id || uuidv4();
                      next.data = data;
                      newNode.routes.static[route] = next;
                      return newNode;
                    });
                  }}
                  checked={node?.routes?.static[route]?.data?.is_enabled_cbw || false}
                />
              }
              label={<Typography sx={controlLabelSx}>Enable Conversation Buffer Window</Typography>}
            />

            <Box>
              <Typography sx={{...sectionLabelSx, mb: 0.5}}>Tags</Typography>
              <Stack direction="row" alignItems="flex-start" gap={0.75}>
                <Box sx={{flex: 1, minWidth: 0}}>
                  <MultiSelectAutocomplete
                    options={project?.settings?.tags || []}
                    selectedValues={node?.routes?.static[route]?.data?.tags || null}
                    placeholder="Select a tag..."
                    getOptionLabel={(opt) => opt.name}
                    getOptionValue={(opt) => opt.id}
                    size="small"
                    multiple
                    blurOnSelect={false}
                    onChange={(value) => {
                      setNode(prev => {
                        const newNode = {...prev};
                        let next = {...newNode.routes.static[route]};
                        if (!next) {
                          next = createRoute(route);
                        }
                        const data = {...next.data};
                        data.tags = value || [];

                        next.id = next?.id || uuidv4();
                        next.data = data;
                        newNode.routes.static[route] = next;
                        return newNode;
                      });
                    }}
                    onBlur={() => onUpdate(node)}
                  />
                </Box>
                <Tooltip title="Manage tags" disableInteractive>
                  <IconButton
                    onClick={() => setManageTagsOpen(true)}
                    sx={{color: UI.muted, "&:hover": {backgroundColor: rgba(UI.accent, 0.1), color: UI.accent}}}
                  >
                    <HugeiconsIcon icon={Settings01Icon} size={17}/>
                  </IconButton>
                </Tooltip>
              </Stack>
            </Box>

            {node?.routes?.static[route]?.data?.is_enabled_cbw ? (
              <TextField
                label="Conversation Buffer Window"
                size="small"
                type="number"
                fullWidth
                sx={fieldSx}
                value={node?.routes?.static[route]?.data?.conversation_buffer_window || ""}
                onChange={(e) => {
                  setNode(prev => {
                    const newNode = {...prev};
                    let next = {...newNode.routes.static[route]};
                    if (!next) {
                      next = createRoute(route);
                    }
                    const data = {...next.data};
                    data.conversation_buffer_window = e.target.value;

                    next.id = next?.id || uuidv4();
                    next.data = data;
                    newNode.routes.static[route] = next;
                    return newNode;
                  });
                }}
                onBlur={() => onUpdate(node)}
              />
            ) : null}

            <FormControlLabel
              sx={{ml: -0.5, mr: 0}}
              control={
                <Checkbox
                  size="small"
                  sx={checkboxSx}
                  onChange={(e, value) => {
                    setNode(prev => {
                      const newNode = {...prev};
                      let next = {...newNode.routes.static[route]};
                      if (!next) {
                        next = createRoute(route);
                      }
                      next.id = next?.id || uuidv4();
                      next.backstack = value;
                      newNode.routes.static[route] = next;
                      if (newNode !== node) {
                        onUpdate(newNode);
                      }
                      return newNode;
                    });
                  }}
                  checked={node?.routes?.static[route]?.backstack || false}
                />
              }
              label={<Typography sx={controlLabelSx}>Backstack</Typography>}
            />

            <RouteParamsList
              params={node?.routes?.static[route]?.data?.params || []}
              onAdd={() => {
                setNode(prev => {
                  const newNode = {...prev};
                  let next = {...newNode.routes.static[route]};
                  if (!next) {
                    next = createRoute(route);
                  }
                  const data = {...next.data};
                  data.params = [...data?.params || [], ""];
                  next.id = next?.id || uuidv4();
                  next.data = data;
                  newNode.routes.static[route] = next;
                  return newNode;
                });
              }}
              onChange={(index, val) => {
                setNode(prev => {
                  const newNode = {...prev};
                  let next = {...newNode.routes.static[route]};
                  if (!next) {
                    next = createRoute(route);
                  }
                  const data = {...next.data};
                  data.params[index] = val;
                  next.id = next?.id || uuidv4();
                  next.data = data;
                  newNode.routes.static[route] = next;

                  if (newNode !== node) {
                    onUpdate(newNode);
                  }
                  return newNode;
                });
              }}
              onRemove={(index) => {
                setNode(prev => {
                  const newNode = {...prev};
                  let next = {...newNode.routes.static[route]};
                  if (!next) {
                    next = createRoute(route);
                  }
                  const data = {...next.data};
                  data.params = data?.params?.filter((p, ind) => ind !== index);
                  next.id = next?.id || uuidv4();
                  next.data = data;
                  newNode.routes.static[route] = next;

                  if (newNode !== node) {
                    onUpdate(newNode);
                  }
                  return newNode;
                });
              }}
            />
          </RouteSection>
        );
      })}
    </>
  );
};
